import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Drawer,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Box,
  styled,
  alpha,
  IconButton,

  Badge,
  Avatar,
  Menu,
  MenuItem,
  Divider,
} from '@mui/material';
import { Menu as MenuIcon } from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useSidebar } from '../../context/SidebarContext';
import { User } from '../../types/auth';
import { useTheme as useMaterialTheme } from '@mui/material';
import { useTheme as useAppTheme } from '../../context/ThemeContext';
import { getOrganizationTrials, getUserData } from '../../utils/encryption';
import { getTrialBadgeText, getTrialBadgeVariant } from '../../utils/trialUIHelpers';
import { isSecurityTrialExpired, isSubscribedUser, isTrialUser } from '../../utils/trialUtils';
import {
  Dashboard,
  Lock,
  Extension,
  Code,
  Api,
  Key,
  Analytics,
  Folder,
  Security,
  Cloud,
  InsertDriveFile,
  Person,
  Notifications,
  ExitToApp,
  DarkMode,
  LightMode,
  Hub,
  Help,
  TrendingUp,
} from '@mui/icons-material';
import ForumIcon from '@mui/icons-material/Forum';
import { NotificationPanel } from './NotificationPanel';
import { useNotifications } from '../../context/NotificationContext';
import { useWalkthrough } from '../../context/WalkthroughContext';
import HelpMenu from '../common/HelpMenu';
import UpgradeDialog from '../common/UpgradeDialog';
import TrialStatsDialog from '../common/TrialStatsDialog';
import { useJira } from '../../context/JiraContext';
import JiraIcon from '../../assets/icons/jira.png';
import { logout } from '../../services/api';

const DRAWER_WIDTH = 240;
const COLLAPSED_DRAWER_WIDTH = 68;

const StyledDrawer = styled(Drawer)<{ open: boolean }>(({ theme, open }) => ({
  width: open ? DRAWER_WIDTH : COLLAPSED_DRAWER_WIDTH,
  flexShrink: 0,
  '& .MuiDrawer-paper': {
    width: open ? DRAWER_WIDTH : COLLAPSED_DRAWER_WIDTH,
    boxSizing: 'border-box',
    background: theme.palette.mode === 'dark' ? '#111111' : 'rgba(248, 250, 252, 0.8)',
    backdropFilter: 'blur(10px)',
    borderRight: `1px solid ${theme.customPalette.border.subtle}`,
    transition: 'width 100ms ease-in-out',
    '&::-webkit-scrollbar': {
      display: 'none',
    },
    scrollbarWidth: 'none',
    msOverflowStyle: 'none',
  },
}));



const StyledListItemButton = styled(ListItemButton)<{ open?: boolean }>(({ theme, open }) => ({
  margin: '0.5px 6px',
  width: open ? 'calc(100% - 12px)' : '40px',
  minWidth: '40px',
  height: '40px',
  borderRadius: '10px',
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  padding: 0,
  overflow: 'hidden',
  transition: 'none',
  backgroundColor: 'transparent',
  '& > *, & .MuiSvgIcon-root, & .MuiListItemText-root': {
    transition: 'none !important'
  },
  '&:hover': {
    backgroundColor: `${alpha(theme.palette.primary.main, 0.08)} !important`,
  },
  '&.Mui-selected': {
    backgroundColor: `${alpha(theme.palette.primary.main, 0.12)} !important`,
    '&:hover': {
      backgroundColor: `${alpha(theme.palette.primary.main, 0.16)} !important`,
    }
  }
}));

const IconContainer = styled('div')(({ theme }) => ({
  position: 'absolute',
  left: 0,
  top: 0,
  width: '40px',
  height: '40px',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  color: theme.palette.text.secondary,
  '& .MuiSvgIcon-root': {
    fontSize: 20,
    transition: 'none !important',
  },
  '& *': {
    transition: 'none !important'
  }
}));

const StyledText = styled(ListItemText)<{ open?: boolean }>(({ theme, open }) => ({
  position: 'absolute',
  left: '40px',
  top: '42%',
  transform: 'translateY(-50%)',
  opacity: open ? 1 : 0,
  visibility: open ? 'visible' : 'hidden',
  transition: 'none',
  whiteSpace: 'nowrap',
  '& .MuiListItemText-primary': {
    fontSize: '0.875rem',
  }
}));

const StyledBadge = styled(Badge)(({ theme }) => ({
  '& .MuiBadge-badge': {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
  },
}));

const navigationItems = [
  {
    label: 'Dashboard',
    icon: <Dashboard />,
    path: '/dashboard'
  },
  {
    label: 'Projects',
    icon: <Folder />,
    path: '/projects'
  },
  {
    label: 'Threat Modeling',
    icon: <Lock />,
    path: '/threat-modeling'
  },
  {
    label: 'SBOM',
    icon: <Extension />,
    path: '/sbom'
  },
  {
    label: 'SCA',
    icon: <Analytics />,
    path: '/sca'
  },
  {
    label: 'Secrets Detection',
    icon: <Key />,
    path: '/secrets'
  },
  {
    label: 'SAST',
    icon: <Code />,
    path: '/sast'
  },
  {
    label: 'API Security',
    icon: <Api />,
    path: '/api-security'
  },
  {
    label: 'DAST',
    icon: <Security />,
    path: '/dast'
  },
  {
    label: 'Infrastructure Scan',
    icon: <Cloud />,
    path: '/infra'
  },
  {
    label: 'Integration',
    icon: <Hub />,
    path: '/integration'
  },
  {
    label: 'Reports',
    icon: <InsertDriveFile />,
    path: '/reports'
  },
  {
    label: 'Ask Ana',
    icon: <ForumIcon />,
    path: '/help'
  },
  {
   label: 'Jira',
   icon: <img src={JiraIcon} alt="Jira" style={{ width: 20, height: 20 }} />,
   path: '/jira',
   connected: true // This will be based on the connection status
 },
];

const Sidebar = () => {
  const theme = useMaterialTheme();
  const { theme: currentTheme, toggleTheme } = useAppTheme();
  const { isOpen, setIsOpen } = useSidebar();
  const { jiraConnected, checkJiraAuth } = useJira();
  
  // Check Jira connection status when sidebar mounts
  useEffect(() => {
    // Only check Jira connection status once when the component mounts
    checkJiraAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    getOrganizationTrials();
  }, []);
  const navigate = useNavigate();
  const location = useLocation();
  const [menuAnchorEl, setMenuAnchorEl] = React.useState<null | HTMLElement>(null);
  const [helpMenuAnchorEl, setHelpMenuAnchorEl] = React.useState<null | HTMLElement>(null);
  const [notificationOpen, setNotificationOpen] = React.useState(false);
  const [upgradeDialogOpen, setUpgradeDialogOpen] = React.useState(false);
  const [trialStatsDialogOpen, setTrialStatsDialogOpen] = React.useState(false);
  const { notifications, markAllAsRead: handleMarkAllAsRead, total, page, setPage } = useNotifications();
  const { startWalkthrough } = useWalkthrough();

  // Get user data for trial system
  const [user, setUser] = React.useState<User | null>(null);
  React.useEffect(() => {
    const userData = getUserData();
    setUser(userData);
    console.log(user?.user_type)
  }, [user?.user_type]);

  // Get trial state
  const hasActiveTrial = user && isTrialUser() && !isSecurityTrialExpired();

  const trialBadgeVariant = user ? getTrialBadgeVariant(user) : 'error';
  const trialBadgeText = user ? getTrialBadgeText(user) : '';


  // Get trial status colors based on the badge variant
  const getTrialStatusColors = () => {
    switch (trialBadgeVariant) {
      case 'success':
        return {
          background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
          hoverBackground: 'linear-gradient(135deg, #16a34a 0%, #22c55e 100%)',
          boxShadow: '0 4px 16px rgba(34, 197, 94, 0.4)'
        };
      case 'warning':
        return {
          background: 'linear-gradient(135deg, #eab308 0%, #ca8a04 100%)',
          hoverBackground: 'linear-gradient(135deg, #ca8a04 0%, #eab308 100%)',
          boxShadow: '0 4px 16px rgba(234, 179, 8, 0.4)'
        };
      case 'error':
        return {
          background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
          hoverBackground: 'linear-gradient(135deg, #dc2626 0%, #ef4444 100%)',
          boxShadow: '0 4px 16px rgba(239, 68, 68, 0.4)'
        };
      case 'info':
      default:
        return {
          background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
          hoverBackground: 'linear-gradient(135deg, #1d4ed8 0%, #3b82f6 100%)',
          boxShadow: '0 4px 16px rgba(59, 130, 246, 0.4)'
        };
    }
  };
  const trialColors = getTrialStatusColors();

  const handleLogout = () => {
    logout();
    setMenuAnchorEl(null);
    navigate('/login');
  };

  const handleTakeTour = () => {
    startWalkthrough();
  };

  const handleDocumentation = () => {
    // Open documentation in new tab
    window.open('https://docs.security.openana.ai', '_blank');
  };

  const handleContactSupport = () => {
    // Open support contact form or email
    window.open('mailto:support@openana.com', '_blank');
  };

  // Navigation is always enabled

  const isActive = (path: string) => {
    if (path === '/projects' && location.pathname.startsWith('/project/')) {
      return true;
    }
    if (path === '/jira' && location.pathname.startsWith('/jira/')) {
      return true;
    }
    return location.pathname === path ||
      (path !== '/' && location.pathname.startsWith(path));
  };

  const toggleDrawer = () => {
    setIsOpen(!isOpen);
  };

  const unreadCount = notifications.filter(n => !n.is_read).length;

  const getUserInitial = () => {
    try {
      const user = getUserData();
      if (!user) return 'U';
      const firstName = user.first_name?.split(' ')[0] || 'User';
      return firstName.charAt(0).toUpperCase();
    } catch (error) {
      return 'U';
    }
  };

  return (
    <StyledDrawer
      variant="permanent"
      open={isOpen}
      sx={{
        '& .MuiDrawer-paper': {
          position: 'fixed',
          left: 0,
          top: 0,
          bottom: 0,
          zIndex: theme.zIndex.drawer,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }
      }}
    >
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflow: 'hidden', // Prevent outer box from scrolling
        position: 'relative'
      }}>
        <Box sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 2,
          background: theme.palette.mode === 'dark' ? '#111111' : 'rgba(248, 250, 252, 0.8)',
          backdropFilter: 'blur(10px)',
        }}>
          <Box sx={{
            minHeight: "72px",
            px: isOpen ? 2 : 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: isOpen ? 'space-between' : 'center',
            borderBottom: `1px solid ${theme.customPalette.border.subtle}`,
            mb: 1,
            position: 'relative'
          }}>

            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', width: isOpen ? '100%' : 'auto' }}>
              {isOpen && (
                <motion.div
                  key="full-logo"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{
                    duration: 0.3,
                    ease: "easeInOut"
                  }}
                  style={{ display: 'flex', alignItems: 'center', gap: '12px' }}
                >
                  <img
                    src="/images/ana_logo.png"
                    alt="ANA Logo"
                    style={{ height: '28px', width: 'auto' }}
                  />
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <img
                      src={theme.palette.mode === 'dark' ? "/images/ana_text_dark.svg" : "/images/ana_text.svg"}
                      alt="Ana"
                      style={{ height: '20px', width: 'auto' }}
                    />
                  </Box>
                </motion.div>
              )}
            </Box>

            <IconButton
              onClick={toggleDrawer}
              sx={{
                color: theme.palette.text.secondary,
                transform: isOpen ? 'rotate(0deg)' : 'rotate(180deg)',
                width: '32px',
                height: '32px',
                transition: theme.transitions.create(['transform', 'color', 'background-color'], {
                  duration: theme.transitions.duration.standard,
                  easing: theme.transitions.easing.easeInOut,
                }),
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.1),
                  color: theme.palette.primary.main,
                  '& .MuiSvgIcon-root': {
                    filter: `drop-shadow(0 0 8px ${theme.customPalette.glow.primary})`
                  }
                },
                '& .MuiSvgIcon-root': {
                  fontSize: '20px'
                }
              }}
            >
              <MenuIcon />
            </IconButton>
          </Box>



        </Box>
        {/* All navigation items including bottom section */}
        <Box
          sx={{
            position: 'absolute',
            top: '70px', // Increased top spacing when closed
            bottom: 0,
            left: 0,
            right: 0,
            overflowY: 'auto',
            overflowX: 'hidden',
            '&::-webkit-scrollbar': {
              display: 'none'
            },
            scrollbarWidth: 'none',
          }}
        >
          <List sx={{
            p: isOpen ? 2 : 0,
            pt: 2, // Increased top padding when closed
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            '& .MuiListItem-root': {
              display: 'flex',
              justifyContent: 'center',
              width: '100%',
              mb: 0.25,
              px: isOpen ? 0 : 1,
              '&:hover': {
                background: 'transparent'
              }
            }
          }}>
            {navigationItems.map((item) => {
             if (item.path === '/jira' && !jiraConnected) {
               return null;
             }
             return (
              <ListItem
                key={item.path}
                disablePadding
                sx={{
                  '&:hover': {
                    background: 'transparent !important'
                  },
                  background: 'transparent !important'
                }}
              >
                <StyledListItemButton
                  open={isOpen}
                  selected={isActive(item.path)}
                  onClick={() => navigate(item.path)}
                  sx={{}}
                >
                  <IconContainer>
                    {item.icon}
                  </IconContainer>
                  {isOpen && (
                    <StyledText
                      open={isOpen}
                      primary={item.label}
                      sx={{
                        '& .MuiListItemText-primary': {
                          fontWeight: isActive(item.path) ? 600 : 400,
                        }
                      }}
                    />
                  )}
                </StyledListItemButton>
              </ListItem>
             );
           })}

            <Box sx={{
              width: '100%',
              my: 2,
              px: 2,
              '&:hover': {
                background: 'transparent'
              }
            }}>
              <Divider sx={{
                borderColor: theme.customPalette.border.subtle
              }} />
            </Box>

            <ListItem
              disablePadding
              sx={{
                '&:hover': {
                  background: 'transparent !important'
                },
                background: 'transparent !important'
              }}
            >
              <StyledListItemButton
                open={isOpen}
                onClick={() => toggleTheme()}
              >
                <IconContainer>
                  {currentTheme === 'dark' ? <LightMode /> : <DarkMode />}
                </IconContainer>
                {isOpen && (
                  <StyledText
                    open={isOpen}
                    primary={currentTheme === 'dark' ? 'Light Mode' : 'Dark Mode'}
                  />
                )}
              </StyledListItemButton>
            </ListItem>

            <ListItem
              disablePadding
              sx={{
                '&:hover': {
                  background: 'transparent !important'
                },
                background: 'transparent !important'
              }}
            >
              <StyledListItemButton
                open={isOpen}
                selected={notificationOpen}
                onClick={() => setNotificationOpen(true)}
                sx={{}}
              >
                <IconContainer>
                  <StyledBadge badgeContent={unreadCount} color="error">
                    <Notifications />
                  </StyledBadge>
                </IconContainer>
                {isOpen && (
                  <StyledText
                    open={isOpen}
                    primary="Notifications"
                  />
                )}
              </StyledListItemButton>
            </ListItem>

            <ListItem
              disablePadding
              sx={{
                '&:hover': {
                  background: 'transparent !important'
                },
                background: 'transparent !important'
              }}
            >
              <StyledListItemButton
                open={isOpen}
                selected={Boolean(helpMenuAnchorEl)}
                onClick={(event) => setHelpMenuAnchorEl(event.currentTarget)}
              >
                <IconContainer>
                  <Help />
                </IconContainer>
                {isOpen && (
                  <StyledText
                    open={isOpen}
                    primary="Help"
                  />
                )}
              </StyledListItemButton>
            </ListItem>

            <ListItem
              disablePadding
              sx={{
                '&:hover': {
                  background: 'transparent !important'
                },
                background: 'transparent !important'
              }}
            >
              <StyledListItemButton
                open={isOpen}
                onClick={(event) => setMenuAnchorEl(event.currentTarget)}
              >
                <IconContainer>
                  <Avatar
                    sx={{
                      width: 26,
                      height: 26,
                      fontSize: '0.9rem',
                      bgcolor: theme.palette.primary.main,
                      border: `2px solid ${theme.customPalette.border.subtle}`,
                    }}
                  >
                    {getUserInitial()}
                  </Avatar>
                </IconContainer>
                {isOpen && (
                  <StyledText
                    open={isOpen}
                    primary="Profile"
                  />
                )}
              </StyledListItemButton>
            </ListItem>

            {/* Trial Stats / Upgrade Button - Hidden for subscribed users */}
            {isTrialUser() && (
              <ListItem
                disablePadding
                sx={{
                  '&:hover': {
                    background: 'transparent !important'
                  },
                  background: 'transparent !important',
                  mt: 1,
                }}
              >
                <StyledListItemButton
                  open={isOpen}
                  onClick={() =>
                    hasActiveTrial
                      ? setTrialStatsDialogOpen(true)
                      : setUpgradeDialogOpen(true)
                  }
                  sx={{
                    background: trialColors.background,
                    '&:hover': {
                      background: trialColors.hoverBackground,
                      transform: 'translateY(-1px)',
                      boxShadow: trialColors.boxShadow,
                    },
                    borderRadius: '10px',
                    marginTop: '8px',
                    marginBottom: '4px',
                    margin: isOpen ? '8px 6px 4px 6px' : '8px auto 4px auto',
                    width: isOpen ? 'calc(100% - 12px)' : '40px',
                    display: 'flex',
                    justifyContent: isOpen ? 'flex-start' : 'center',
                    position: 'relative',
                  }}
                >
                  <div
                    style={{
                      width: '40px',
                      height: '40px',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      marginLeft: isOpen ? '15px' : '0',
                    }}
                  >
                    <Box
                      sx={{
                        background: trialColors.background,
                        borderRadius: '50%',
                        width: '26px',
                        height: '26px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                      }}
                    >
                      {hasActiveTrial ? <TrendingUp sx={{ fontSize: 16 }} /> : '↑'}
                    </Box>
                  </div>

                  {isOpen && (
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        marginLeft: '16px',
                        justifyContent: 'center',
                        width: 'calc(100% - 86px)',
                      }}
                    >
                      <div
                        style={{
                          fontWeight: 600,
                          color: 'white',
                          fontSize: '0.875rem',
                          lineHeight: '1.2',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {hasActiveTrial ? 'Trial Stats' : 'Upgrade'}
                      </div>
                      {hasActiveTrial && trialBadgeText && (
                        <div
                          style={{
                            fontSize: '0.75rem',
                            color: 'white',
                            opacity: 0.9,
                            lineHeight: '1.2',
                            marginTop: '2px',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {trialBadgeText}
                        </div>
                      )}
                    </div>
                  )}
                </StyledListItemButton>
              </ListItem>
            )}
            {isSubscribedUser() && (
              <ListItem
                disablePadding
                sx={{
                  '&:hover': {
                    background: 'transparent !important',
                  },
                  background: 'transparent !important',
                  mt: 1,
                }}
              >
                <StyledListItemButton
                  onClick={() => setTrialStatsDialogOpen(true)}
                  sx={{
                    background: 'linear-gradient(135deg, #60a5fa 0%, #3b82f6 100%) !important',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #3b82f6 0%, #60a5fa 100%) !important',
                      transform: 'translateY(-1px)',
                      boxShadow: '0 4px 16px rgba(59, 130, 246, 0.4)',
                    },
                    borderRadius: '10px',
                    margin: '8px 6px 4px 6px',
                    width: 'calc(100% - 12px)',
                    display: 'flex',
                    justifyContent: 'flex-start',
                    position: 'relative',
                  }}
                >
                  <div style={{
                    width: '40px',
                    height: '40px',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    marginLeft: '15px',
                  }}>
                    <Box
                      sx={{
                        background: 'linear-gradient(135deg, #60a5fa 0%, #3b82f6 100%) !important',
                        borderRadius: '50%',
                        width: '26px',
                        height: '26px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                      }}
                    >
                      {hasActiveTrial ? <TrendingUp sx={{ fontSize: 16 }} /> : '↑'}
                    </Box>
                  </div>
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    marginLeft: '16px',
                    justifyContent: 'center',
                    width: 'calc(100% - 86px)',
                  }}>
                    <div style={{
                      fontWeight: 600,
                      color: 'white',
                      fontSize: '0.875rem',
                      lineHeight: '1.2',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}>
                      Usage Stats
                    </div>
                  </div>
                </StyledListItemButton>
              </ListItem>
            )}

          </List>

          <NotificationPanel
            isOpen={notificationOpen}
            onClose={() => setNotificationOpen(false)}
            notifications={notifications}
            onMarkAllAsRead={handleMarkAllAsRead}
            total={total}
            page={page}
            onPageChange={(newPage) => setPage(newPage)}
          />

          <HelpMenu
            anchorEl={helpMenuAnchorEl}
            open={Boolean(helpMenuAnchorEl)}
            onClose={() => setHelpMenuAnchorEl(null)}
            onTakeTour={handleTakeTour}
            onDocumentation={handleDocumentation}
            onContactSupport={handleContactSupport}
          />

          {/* Upgrade Dialog */}
          <UpgradeDialog
            open={upgradeDialogOpen}
            onClose={() => setUpgradeDialogOpen(false)}
          />

          {/* Trial Stats Dialog */}
          <TrialStatsDialog
            open={trialStatsDialogOpen}
            onClose={() => setTrialStatsDialogOpen(false)}
            user={user}
          />

        </Box>

        {/* Profile Menu - moved outside scrollable container */}
        <Menu
          anchorEl={menuAnchorEl}
          open={Boolean(menuAnchorEl)}
          onClose={() => setMenuAnchorEl(null)}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          PaperProps={{
            sx: {
              mt: 1,
              zIndex: theme.zIndex.modal + 1,
              background: theme.palette.mode === 'dark'
                ? 'rgba(17, 17, 17, 0.95)'
                : 'rgba(248, 250, 252, 0.95)',
              backdropFilter: 'blur(10px)',
              border: `1px solid ${theme.customPalette?.border?.subtle || (theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)')}`,
              boxShadow: theme.palette.mode === 'dark'
                ? '0 8px 32px rgba(0, 0, 0, 0.4)'
                : '0 8px 32px rgba(0, 0, 0, 0.1)',
            }
          }}
        >
          <MenuItem onClick={() => {
            setMenuAnchorEl(null);
            navigate('/profile');
          }}>
            <Person sx={{ mr: 1.5 }} />
            Profile
          </MenuItem>
          {/* <MenuItem onClick={() => {
            setMenuAnchorEl(null);
            navigate('/settings');
          }}>
            <Settings sx={{ mr: 1.5 }} />
            Settings
          </MenuItem> */}
          <MenuItem onClick={handleLogout} sx={{ color: 'error.main' }}>
            <ExitToApp sx={{ mr: 1.5 }} />
            Logout
          </MenuItem>
        </Menu>
      </Box>
    </StyledDrawer>
  );
};

export default Sidebar;
